import type { RescheduleInstanceResult, ScheduledInstance } from './transportsSlice';

/** Shared copy for guiding operators to set mandatory params via Update. */
export const MANDATORY_PARAM_UPDATE_GUIDANCE =
  'Open each affected instance from Node Instances (⋮ → Update) and set the new parameter(s) before the run starts.';

export function hasMissingMandatoryParameters(si: Pick<ScheduledInstance, 'missing_mandatory_parameters'>): boolean {
  return !!si.missing_mandatory_parameters?.length;
}

export function countInstancesWithMissingMandatoryParameters(instances: ScheduledInstance[]): number {
  return instances.filter(hasMissingMandatoryParameters).length;
}

export type RescheduleWarning = NonNullable<RescheduleInstanceResult['warnings']>[number];

export function collectMandatoryParameterWarnings(results: RescheduleInstanceResult[]): RescheduleWarning[] {
  return results.flatMap((r) => r.warnings ?? []).filter((w) => w.code === 'MISSING_MANDATORY_PARAMETERS');
}

/** Distinct instance ids listed in nested missing_mandatory_parameters arrays. */
export function countAffectedMandatoryParameterInstances(warnings: RescheduleWarning[]): number {
  const ids = warnings.flatMap((w) => (w.missing_mandatory_parameters ?? []).map((m) => m.node_instance_id));
  return new Set(ids).size || warnings.length;
}

export function summarizeWithLimit<T>(items: T[], limit: number, formatItem: (item: T) => string): string {
  if (items.length === 0) return '';
  const head = items.slice(0, limit).map(formatItem).join('; ');
  const extra = items.length > limit ? ` (+${items.length - limit} more)` : '';
  return `${head}${extra}`;
}

export function formatMandatoryParameterWarningDetail(warnings: RescheduleWarning[], limit = 5): string {
  const entries = warnings.flatMap((w) =>
    (w.missing_mandatory_parameters ?? []).map((m) => ({
      node_instance_id: m.node_instance_id,
      parameters: m.parameters,
    }))
  );
  if (entries.length === 0) {
    const messages = warnings.map((w) => w.message).filter(Boolean);
    return messages[0] ?? '';
  }
  return summarizeWithLimit(entries, limit, (e) => `#${e.node_instance_id}${e.parameters.length ? `: ${e.parameters.join(', ')}` : ''}`);
}

export function buildMandatoryParameterRescheduleSentence(updated: number, affectedCount: number, warnings: RescheduleWarning[]): string {
  const detail = formatMandatoryParameterWarningDetail(warnings);
  const countPhrase = `${affectedCount} instance(s) still need mandatory parameter value(s)`;
  return detail
    ? `${updated} instance(s) updated, but ${countPhrase} (${detail}). ${MANDATORY_PARAM_UPDATE_GUIDANCE}`
    : `${updated} instance(s) updated, but ${countPhrase}. ${MANDATORY_PARAM_UPDATE_GUIDANCE}`;
}

export function buildRescheduleToastMessage(results: RescheduleInstanceResult[]): string {
  const updated = results.filter((r) => r.status === 'updated').length;
  const skipped = results.filter((r) => r.status === 'skipped');
  const mandatoryWarnings = collectMandatoryParameterWarnings(results);
  const parts: string[] = [];

  if (skipped.length > 0) {
    const reasons = summarizeWithLimit(skipped, 5, (r) => `#${r.node_instance_id}${r.reason ? `: ${r.reason}` : ''}`);
    parts.push(`${updated} instance(s) updated, ${skipped.length} skipped. ${reasons}`);
  }

  if (mandatoryWarnings.length > 0) {
    const affectedCount = countAffectedMandatoryParameterInstances(mandatoryWarnings);
    parts.push(buildMandatoryParameterRescheduleSentence(updated, affectedCount, mandatoryWarnings));
  }

  if (parts.length === 0) {
    return `${updated} instance(s) updated successfully!`;
  }

  return parts.join(' ');
}

export function buildConfirmImportToastMessage(scheduledInstances: ScheduledInstance[]): string {
  if (scheduledInstances.length === 0) {
    return 'Import completed successfully!';
  }

  const missingCount = countInstancesWithMissingMandatoryParameters(scheduledInstances);
  if (missingCount > 0) {
    return (
      `Import completed. ${scheduledInstances.length} scheduled instance(s) may need updating. ` +
      `${missingCount} have new mandatory parameter(s) without a value — set them via ⋮ → Update after updating the instance.`
    );
  }

  return `Import completed! ${scheduledInstances.length} scheduled instance(s) may need updating.`;
}

export function missingMandatoryParameterBadgeLabel(count: number): string {
  return count === 1 ? 'Missing 1 param' : `Missing ${count} params`;
}
